// Where Bomb the Hill's people go off the road (#202, the rest), sim- and
// three.js-free. The sim only needs a walker's x on the road (`walkerX`);
// off it, past the dropped kerb, `walkerSpot` turns the same cycle into a
// walk to somewhere: a shopfront behind each crosswalk, its door at the end
// of the footpath and one either side at the ends of the pavement. Half the
// groups come and go straight up the footpath, the rest along the pavement
// and in at a side door, so people reach the crossing along the pavement
// too. Indoors they're out of sight. Render-only: nothing here changes what
// the rider can hit, when, or how the course lays out.

import { CROSSING, walkerX, type HillCourse, type HillCrossing, type HillWalker } from "./hill-course";

/** The shopfront behind each crosswalk (m): its front `front` out past the road's edge, a door at the footpath's end and one `side` either way along it at the pavement's ends. */
export const SHOPFRONT = {
  front: 7.5,
  /** How far along the road from the crossing's middle (beyond its half-length) the side doors are. */
  side: 4,
  /** How far out past the road's edge people walk along the pavement. */
  pavement: 1.9,
  /** How far either side of the crossing's middle (beyond its half-length) the shopfront runs. */
  reach: 5,
  /** How tall it is. */
  h: 4.2,
  /** How deep it is, back from its front. */
  deep: 4,
} as const;

/** Where someone on a crosswalk is drawn: on the road, the pavement or the footpath, or indoors (`inside`, out of sight). */
export interface WalkerSpot {
  x: number;
  z: number;
  inside: boolean;
}

/** A walker's way off the road: straight up the footpath, or along the pavement one way or the other (-1 up the hill, 1 down) to a side door. */
export type WalkRoute = 0 | -1 | 1;

/** Which way a walker's group comes and goes (#202): its people share their keys, so they share a route. */
export function routeOf(w: HillWalker): WalkRoute {
  const n = Math.floor((w.keys[1]?.t ?? 0) * 1000) % 4;
  return n < 2 ? 0 : n === 2 ? -1 : 1;
}

/** The route's points (x out from the road's edge, z along from the walker's own z), from the kerb to its door, on the +x side. */
function routePoints(route: WalkRoute, c: { hl: number }): readonly [number, number][] {
  const kerb = CROSSING.kerb;
  if (route === 0) return [
    [kerb, 0],
    [SHOPFRONT.front, 0],
  ];
  const along = route * (c.hl + SHOPFRONT.side);
  return [
    [kerb, 0],
    [SHOPFRONT.pavement, 0],
    [SHOPFRONT.pavement, along],
    [SHOPFRONT.front, along],
  ];
}

/** How long a route is (m), kerb to door. */
export function routeLength(route: WalkRoute, c: { hl: number }): number {
  const p = routePoints(route, c);
  let n = 0;
  for (let i = 1; i < p.length; i++) n += Math.hypot(p[i]![0] - p[i - 1]![0], p[i]![1] - p[i - 1]![1]);
  return n;
}

/** The point `d` m along a route from the kerb (clamped to its door). */
function along(route: WalkRoute, c: { hl: number }, d: number): [number, number] {
  const p = routePoints(route, c);
  for (let i = 1; i < p.length; i++) {
    const [ax, az] = p[i - 1]!;
    const [bx, bz] = p[i]!;
    const len = Math.hypot(bx - ax, bz - az);
    if (d <= len || i === p.length - 1) {
      const f = len > 0 ? Math.min(1, Math.max(0, d / len)) : 1;
      return [ax + (bx - ax) * f, az + (bz - az) * f];
    }
    d -= len;
  }
  return p[p.length - 1]!;
}

/**
 * The walker's time off the road around cycle time `t`: how long since they
 * stepped off the kerb (`since`), how long until they're back on it
 * (`until`), and how fast they walk off it (m/s). Null while they're on the
 * road or at the kerb. Their cycle always comes back to the kerb they left
 * (`crowd`), wrapping round its end.
 */
function offRoad(w: HillWalker, t: number, kerb: number): { since: number; until: number; pace: number } | null {
  const k = w.keys;
  const P = w.period;
  const tt = ((t % P) + P) % P;
  const at = (i: number) => k[((i % k.length) + k.length) % k.length]!;
  const on = (i: number) => Math.abs(at(i).x) <= kerb + 1e-6;
  // The key just before tt.
  let i = 0;
  while (i + 1 < k.length && k[i + 1]!.t <= tt) i++;
  const x = keyAt(k, tt);
  if (Math.abs(x) <= kerb + 1e-6) return null;
  // Back to the last key on the kerb, and on to the next one, counting laps of the cycle.
  let a = i;
  let lapA = 0;
  for (let n = 0; n < k.length && !on(a); n++) {
    a--;
    if (a < 0) {
      a = k.length - 1;
      lapA -= P;
    }
  }
  let b = i + 1;
  let lapB = 0;
  for (let n = 0; n < k.length && !on(b); n++) {
    b++;
    if (b >= k.length) {
      b = 0;
      lapB += P;
    }
  }
  if (!on(a) || !on(b)) return null;
  const leave = at(a).t + lapA;
  const back = at(b).t + lapB;
  // Their pace: the leg straight off the kerb.
  const next = at(a + 1);
  const dt = next.t - at(a).t;
  const pace = dt > 0 ? Math.abs(next.x - at(a).x) / dt : CROSSING.pace.min;
  return { since: tt - leave, until: back - tt, pace };
}

/** `keyX` on a cycle time already wrapped into 0 .. period. */
function keyAt(k: HillWalker["keys"], tt: number): number {
  for (let i = 1; i < k.length; i++) {
    const b = k[i]!;
    if (tt <= b.t) {
      const a = k[i - 1]!;
      return b.t > a.t ? a.x + ((b.x - a.x) * (tt - a.t)) / (b.t - a.t) : b.x;
    }
  }
  return k[k.length - 1]!.x;
}

/**
 * Where to draw walker `w` of crossing `c` at sim time `clock` (#202). On
 * the road and at the kerb, where the sim has them. Off it they walk their
 * route at their pace, out to its door and in, and back out of it in time
 * to reach the kerb when the sim has them there; a stop off the road too
 * short to get indoors and back is spent standing where the sim has them
 * on the footpath instead.
 */
export function walkerSpot(course: HillCourse, c: HillCrossing, w: HillWalker, clock: number): WalkerSpot {
  const x = walkerX(course, w, clock);
  const W = course.halfWidth;
  const kerb = W + CROSSING.kerb;
  const off = offRoad(w, clock - course.t0 + w.phase, kerb);
  if (!off) return { x, z: w.z, inside: false };
  const side = Math.sign(x);
  const route = routeOf(w);
  const L = routeLength(route, c);
  const reach = off.pace * Math.min(off.since, off.until);
  // Not long enough off the road to get in and back out: as the sim has them, out along the footpath.
  const d = off.pace * (off.since + off.until) < 2 * L ? Math.abs(x) - kerb : Math.min(reach, L);
  const r = off.pace * (off.since + off.until) < 2 * L ? 0 : route;
  const [ox, oz] = along(r, c, d);
  return { x: side * (W + ox), z: w.z + oz, inside: r === route && d >= L };
}

/** Whether anyone's out on the crosswalk `hl` either side of `z` at `clock` (#202): its walk lights are lit. */
export function crossingBusy(course: HillCourse, z: number, hl: number, clock: number): boolean {
  for (const w of course.walkers) {
    if (w.z > z + hl) break;
    if (w.z < z - hl) continue;
    if (Math.abs(walkerX(course, w, clock)) < course.halfWidth) return true;
  }
  return false;
}

/** The crossing walker `w` is on, if any. */
export function crossingOf(course: HillCourse, w: HillWalker): HillCrossing | null {
  for (const c of course.crossings) if (Math.abs(w.z - c.z) <= c.hl) return c;
  return null;
}
